async function request<T>(url: string, init?: RequestInit): Promise<T> {
    // Pull `headers` out so the caller's other options spread first and the
    // merged header object below always wins (a bare `...init` after `headers`
    // would clobber the merge).
    const { headers: extraHeaders, ...rest } = init ?? {};
    const res = await fetch(url, {
        credentials: 'same-origin',
        ...rest,
        headers: {
            Accept: 'application/json',
            ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
            ...(extraHeaders ?? {}),
        },
    });
    if (!res.ok) {
        throw Object.assign(new Error(`HTTP ${res.status} for ${url}`), {
            status: res.status,
        });
    }
    return (await res.json()) as T;
}

// Short-lived GET cache, keyed by URL. The stored value is the promise itself,
// so it covers both cases uniformly:
//   - still loading: a second request for the same URL (e.g. after navigating
//     away and back) reuses the in-flight promise instead of restarting — the
//     load keeps going and finishes once, wherever you are.
//   - already loaded: returning to the page within FRESH_MS resolves instantly.
// A loaded entry older than FRESH_MS is fetched again, so an app left open
// still shows a newly published album the next time it opens the home or a
// year page. Entries are also dropped by a full reload, by a mutation (see
// apiPost/apiPut) and after a long absence (appRefresh). Failed requests are
// evicted so they can be retried.
const FRESH_MS = 60_000;
const getCache = new Map<string, { promise: Promise<unknown>; loadedAt: number | null }>();

export function apiGet<T>(url: string): Promise<T> {
    const cached = getCache.get(url);
    if (cached && (cached.loadedAt === null || Date.now() - cached.loadedAt < FRESH_MS)) {
        return cached.promise as Promise<T>;
    }

    const entry: { promise: Promise<unknown>; loadedAt: number | null } = { promise: Promise.resolve(), loadedAt: null };
    const pending = request<T>(url).then(
        (data) => {
            entry.loadedAt = Date.now();
            return data;
        },
        (err) => {
            if (getCache.get(url) === entry) getCache.delete(url); // don't cache failures — allow a retry
            throw err;
        },
    );
    entry.promise = pending;
    getCache.set(url, entry);
    return pending;
}

/** Drop all cached GETs (e.g. after a mutation) so the next read is fresh. */
export function clearApiCache(): void {
    getCache.clear();
}

// A write may change anything the server would return. Invalidate immediately
// (so a GET fired before the write resolves — e.g. navigating to Favorites
// right after liking — can't be served a stale cached entry) and again on
// settle (to drop anything re-cached while the write was in flight).
function mutate<T>(method: 'POST' | 'PUT', url: string, body?: unknown): Promise<T> {
    clearApiCache();
    return request<T>(url, {
        method,
        body: body === undefined ? null : JSON.stringify(body),
    }).finally(clearApiCache);
}

export const apiPost = <T>(url: string, body?: unknown): Promise<T> => mutate<T>('POST', url, body);
export const apiPut = <T>(url: string, body?: unknown): Promise<T> => mutate<T>('PUT', url, body);

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '') continue;
        usp.set(k, String(v));
    }
    const s = usp.toString();
    return s ? `?${s}` : '';
}
